/**
 * Opus Clip Quick Reference Cheat Sheet
 *
 * The source was a two-page PDF Caren hands out as a bonus. A PDF is the wrong
 * shape for the one thing this content is for — standing next to you while you
 * work — because it cannot tick anything off and it is read on a phone at 40%
 * zoom. So the workflow is a page you scroll and the editing checklist is
 * something you actually check.
 *
 * The document keeps the PDF's three sections and their order, because people
 * who have the printout should recognise the page. What changed is type size
 * and tap targets: the checklist rows are 44px+ and nothing here is under
 * 16px, which the PDF's 11pt bullets were.
 *
 * The checklist deliberately does NOT persist. It is a per-video list — six
 * things to confirm before this clip goes out — and a list that remembers
 * yesterday's ticks is worse than one that starts clean, because the ticks
 * would be lying about the video in front of you. "Start a new clip" is the
 * reset, named for what it means rather than what it does.
 */

import GuideShell from "@/components/GuideShell";
import GuideFrame from "@/components/GuideFrame";

const HTML_SRC = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Opus Clip Quick Reference Cheat Sheet | Caren Glasser</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">

<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --cream: oklch(0.98 0.008 85);
    --linen: oklch(0.93 0.01 85);
    --white: oklch(1 0.004 85);
    --border: oklch(0.88 0.012 85);
    --espresso: oklch(0.22 0.015 65);
    --body: oklch(0.52 0.015 65);
    --muted: oklch(0.52 0.015 65);
    --terracotta: oklch(0.32 0.08 255);
    --terracotta-light: oklch(0.32 0.08 255 / 0.08);
    --terracotta-mid: oklch(0.32 0.08 255 / 0.22);
    --sage: oklch(0.38 0.09 155);
    --sage-light: oklch(0.38 0.09 155 / 0.10);
  }

  body {
    background: var(--cream);
    color: var(--espresso);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 17px;
    line-height: 1.6;
    min-height: 100vh;
    padding: 40px 20px 72px;
  }

  .container { max-width: 700px; margin: 0 auto; }

  /* HEADER */
  .header {
    text-align: center;
    margin-bottom: 40px;
    padding-bottom: 32px;
    border-bottom: 1px solid var(--border);
  }

  .header .eyebrow {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 14px;
  }

  .header h1 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: clamp(28px, 5vw, 38px);
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 14px;
  }

  .header p {
    font-size: 17px;
    color: var(--body);
    max-width: 480px;
    margin: 0 auto;
  }

  .header-actions { margin-top: 24px; }

  /* SECTIONS */
  .section { margin-bottom: 44px; }

  .section-title {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 24px;
    font-weight: 700;
    color: var(--espresso);
    margin-bottom: 6px;
  }

  .section-intro {
    font-size: 16px;
    color: var(--body);
    margin-bottom: 20px;
  }

  /* WORKFLOW STEPS */
  .step {
    display: flex;
    gap: 18px;
    background: var(--white);
    border: 1px solid var(--border);
    padding: 20px 22px;
    margin-bottom: 12px;
  }

  .step-number {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    border-radius: 999px;
    background: var(--terracotta);
    color: var(--cream);
    font-size: 17px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  .step-body { flex: 1; }

  .step-title {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 20px;
    font-weight: 600;
    color: var(--espresso);
    margin-bottom: 8px;
  }

  .step-body ul { list-style: none; }

  .step-body li {
    font-size: 16px;
    color: var(--body);
    padding: 6px 0 6px 20px;
    position: relative;
  }

  .step-body li::before {
    content: "—";
    position: absolute;
    left: 0;
    color: var(--terracotta);
  }

  .step-body li strong { color: var(--espresso); font-weight: 600; }

  /* PLATFORM ROWS */
  .platform {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 14px;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 18px 22px;
    margin-bottom: 10px;
  }

  .platform-name {
    font-size: 17px;
    font-weight: 600;
    color: var(--espresso);
    min-width: 168px;
  }

  .platform-rule { font-size: 16px; color: var(--body); flex: 1; }

  /* CHECKLIST */
  .checklist {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 8px;
    overflow: hidden;
  }

  .check-row {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    min-height: 60px;
    padding: 14px 22px;
    background: none;
    border: none;
    border-bottom: 1px solid var(--border);
    font-family: inherit;
    font-size: 17px;
    color: var(--espresso);
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .check-row:last-child { border-bottom: none; }
  .check-row:hover { background: var(--terracotta-light); }

  .check-box {
    width: 26px;
    height: 26px;
    flex-shrink: 0;
    border: 2px solid var(--border);
    border-radius: 6px;
    background: var(--white);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
  }

  .check-row.done .check-box {
    background: var(--sage);
    border-color: var(--sage);
  }

  .check-box svg {
    opacity: 0;
    transform: scale(0.4);
    transition: all 0.2s ease;
  }

  .check-row.done .check-box svg { opacity: 1; transform: scale(1); }
  .check-row.done .check-label { color: var(--muted); text-decoration: line-through; }

  .check-label { flex: 1; }

  .check-footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 16px;
  }

  .check-count { font-size: 16px; color: var(--body); }
  .check-count span { color: var(--terracotta); font-weight: 700; }

  .completion {
    display: none;
    background: var(--sage-light);
    border: 1px solid var(--sage);
    padding: 20px 24px;
    margin-top: 16px;
  }

  .completion.visible { display: block; }

  .completion p { font-size: 17px; color: var(--espresso); }
  .completion strong { font-weight: 700; }

  /* TIP */
  .tip-box {
    background: var(--linen);
    border-left: 4px solid var(--terracotta);
    padding: 20px 24px;
    margin-top: 8px;
  }

  .tip-box .label {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 8px;
  }

  .tip-box p { font-size: 16px; color: var(--espresso); }

  /* SIGN-OFF
     The site header, wordmark, account control and real footer come from
     GuideShell around this document — the same as every other guide. This is
     only the closing line inside the document, matching the Castmagic guide's
     quote-and-attribution pair. The PDF this came from signed off with a link
     to CarenGlasser.com; that is the one thing not carried over, because a
     guide's last word should not be a link off the site the reader is on. */
  .footer {
    text-align: center;
    margin-top: 48px;
    padding-top: 32px;
    border-top: 1px solid var(--border);
  }

  .footer-quote {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 18px;
    font-style: italic;
    color: var(--muted);
    line-height: 1.6;
    max-width: 480px;
    margin: 0 auto 10px;
  }

  .footer-attr {
    font-size: 12px;
    color: var(--terracotta);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  @media (max-width: 520px) {
    .platform-name { min-width: 100%; }
  }

  /* PRINT: this started life as a printed handout, so it has to stay one. */
  @media print {
    body { background: white; padding: 20px; font-size: 12pt; }
    .header-actions, .check-footer, .completion { display: none !important; }
    .step, .platform, .checklist { break-inside: avoid; border: 1px solid #ccc; }
  }
</style>
</head>
<body>
<div class="container">

  <div class="header">
    <p class="eyebrow">Opus Clip: Quick Reference</p>
    <h1>Opus Clip Cheat Sheet</h1>
    <p>One long video in, a week of short clips out. Here is the whole process on one page, plus the checklist to run before any clip goes out.</p>
    <div class="header-actions">
      <button class="action-btn" onclick="window.print()">Print this cheat sheet</button>
    </div>
  </div>

  <!-- WORKFLOW -->
  <div class="section">
    <h2 class="section-title">Workflow at a glance</h2>
    <p class="section-intro">Six steps, in order. You do not need to finish all six the first time: import and export alone will get a clip posted.</p>

    <div class="step">
      <div class="step-number">1</div>
      <div class="step-body">
        <div class="step-title">Import</div>
        <ul>
          <li>Upload from <strong>Zoom, YouTube, or an MP4</strong> file on your computer</li>
          <li>Let the AI find the hooks and highlights before you touch anything</li>
        </ul>
      </div>
    </div>

    <div class="step">
      <div class="step-number">2</div>
      <div class="step-body">
        <div class="step-title">Edit</div>
        <ul>
          <li>Trim, split, and delete: cut anything that is not the point</li>
          <li>Fix the captions, for accuracy first and readability second</li>
          <li>Add emojis or highlight the keywords you want people to catch</li>
        </ul>
      </div>
    </div>

    <div class="step">
      <div class="step-number">3</div>
      <div class="step-body">
        <div class="step-title">Brand</div>
        <ul>
          <li>Apply your logo, colors, and fonts</li>
          <li>Add a lower third or a call to action</li>
          <li>Save it as a brand kit so you never set it up twice</li>
        </ul>
      </div>
    </div>

    <div class="step">
      <div class="step-number">4</div>
      <div class="step-body">
        <div class="step-title">Enhance with B-roll</div>
        <ul>
          <li>Use auto b-roll, or upload your own clips</li>
          <li>Match the visuals to the nouns and verbs you are saying</li>
          <li>Keep every insert short: <strong>2 to 3 seconds</strong></li>
        </ul>
      </div>
    </div>

    <div class="step">
      <div class="step-number">5</div>
      <div class="step-body">
        <div class="step-title">Export</div>
        <ul>
          <li>Aspect ratio: <strong>9:16</strong> tall for TikTok, Reels and Shorts, <strong>1:1</strong> square for LinkedIn, <strong>16:9</strong> wide for YouTube</li>
          <li>Captions: burned into the video, or as a separate .srt file</li>
          <li>Add a custom thumbnail</li>
        </ul>
      </div>
    </div>

    <div class="step">
      <div class="step-number">6</div>
      <div class="step-body">
        <div class="step-title">Post</div>
        <ul>
          <li>Upload natively to each platform rather than cross-posting one link</li>
          <li>Write the caption as <strong>hook, then value, then call to action</strong></li>
          <li>Use 3 to 5 hashtags: some for your niche, some for the kind of content it is</li>
        </ul>
      </div>
    </div>
  </div>

  <!-- POSTING -->
  <div class="section">
    <h2 class="section-title">Posting best practices</h2>
    <p class="section-intro">The same clip behaves differently on each platform. These are the rules worth following.</p>

    <div class="platform">
      <div class="platform-name">TikTok and Reels</div>
      <div class="platform-rule">20 to 45 seconds, with the hook in the first 3 seconds</div>
    </div>
    <div class="platform">
      <div class="platform-name">YouTube Shorts</div>
      <div class="platform-rule">A title people would search for, added to a playlist, hook up front</div>
    </div>
    <div class="platform">
      <div class="platform-name">LinkedIn</div>
      <div class="platform-rule">Add your own insight in the post, and put the call to action in the comments</div>
    </div>
    <div class="platform">
      <div class="platform-name">Everywhere</div>
      <div class="platform-rule">Post 3 to 5 clips a week, all from the one long video</div>
    </div>

    <div class="tip-box">
      <div class="label">The whole idea</div>
      <p>You are not making five videos a week. You are making one, and Opus Clip is making the other five out of it.</p>
    </div>
  </div>

  <!-- CHECKLIST -->
  <div class="section">
    <h2 class="section-title">Editing checklist</h2>
    <p class="section-intro">Run this before a clip goes out. Tap a line to tick it off. The list starts fresh for each new clip.</p>

    <div class="checklist" id="checklist">
      <button class="check-row" onclick="toggleCheck(this)">
        <span class="check-box"><svg width="14" height="14" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
        <span class="check-label">Strong opening hook</span>
      </button>
      <button class="check-row" onclick="toggleCheck(this)">
        <span class="check-box"><svg width="14" height="14" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
        <span class="check-label">Long pauses removed</span>
      </button>
      <button class="check-row" onclick="toggleCheck(this)">
        <span class="check-box"><svg width="14" height="14" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
        <span class="check-label">Captions accurate and styled</span>
      </button>
      <button class="check-row" onclick="toggleCheck(this)">
        <span class="check-box"><svg width="14" height="14" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
        <span class="check-label">Audio balanced</span>
      </button>
      <button class="check-row" onclick="toggleCheck(this)">
        <span class="check-box"><svg width="14" height="14" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
        <span class="check-label">Brand applied: logo, colors, fonts</span>
      </button>
      <button class="check-row" onclick="toggleCheck(this)">
        <span class="check-box"><svg width="14" height="14" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
        <span class="check-label">Call to action visible</span>
      </button>
    </div>

    <div class="check-footer">
      <p class="check-count"><span id="doneCount">0</span> of 6 checked</p>
      <button class="copy-btn" onclick="resetChecks()">Start a new clip</button>
    </div>

    <div class="completion" id="completion">
      <p><strong>That clip is ready to post.</strong> Now do the same for the next one: same long video, different 30 seconds.</p>
    </div>
  </div>

  <div class="footer">
    <p class="footer-quote">"You already recorded it. This is just the part where more people get to see it."</p>
    <p class="footer-attr">Your Smart Stack &middot; Opus Clip</p>
  </div>

</div>
<script>
  const TOTAL = 6;

  function toggleCheck(row) {
    row.classList.toggle('done');
    updateCount();
  }

  function resetChecks() {
    document.querySelectorAll('.check-row.done').forEach(r => r.classList.remove('done'));
    updateCount();
  }

  function updateCount() {
    const done = document.querySelectorAll('.check-row.done').length;
    document.getElementById('doneCount').textContent = done;
    document.getElementById('completion').classList.toggle('visible', done === TOTAL);
  }
<\/script>
</body>
</html>`;

export default function OpusClipCheatSheet() {
  return (
    <GuideShell
      context={"Opus Clip guide"}
      backHref={"/resources/opus-clip"}
      backLabel={"Back to Opus Clip"}
    >
      <GuideFrame html={HTML_SRC} title={"Opus Clip Quick Reference Cheat Sheet"} />
    </GuideShell>
  );
}
